import React from 'react'
import styled from 'styled-components'
import Text from 'core-system/Text'
import palette from 'core-system/Themes/palette'
import Dropdown from 'core-system/Dropdown/Dropdown'
import {
  getEmployeeSavings,
  getEmployerSavings,
} from 'features/ProgramRecommender/cityCost'
import { formatNumber } from '../Utils'
import Box from '../components/Box'

const StatsContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`

const StatsHeader = styled.div`
  padding: 1.5rem 1.5rem 0 1.5rem;
`

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`

const TitleLeft = styled.div`
  display: flex;
  align-items: center;
`

const DaysInOfficeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const WorksiteOverviewContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-top: 2.5rem;
  margin-bottom: 2.5rem;
`

const OverviewItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.5rem;
`

const OverviewBox = styled.div<{ color: 'gray' | 'green' | 'blue' | 'pink' }>`
  width: 100%;
  padding: 1rem;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 80px;

  ${({ color }) => {
    switch (color) {
      case 'gray':
        return `
          background-color: ${palette.grey.grey100};
          color: ${palette.grey.grey800};
        `
      case 'green':
        return `
          background-color: ${palette.success.green100};
          color: ${palette.success.green800};
        `
      case 'blue':
        return `
          background-color: ${palette.accent.blue100};
          color: ${palette.accent.blue800};
        `
      case 'pink':
        return `
          background-color: ${palette.secondary.purple100};
          color: ${palette.primary.pink600};
        `
      default:
        return ''
    }
  }}
`

interface MapStatsProps {
  carReach: number
  micromobilityReach: number
  transitReach: number
  getCityCosts: (
    city: string,
    daysInOffice: number
  ) => {
    transit: number
    micromobility: number
    car: number
    transitPassType: 'monthly' | 'daily'
    micromobilityPassType: 'monthly' | 'daily'
    carPassType: 'monthly' | 'daily'
  }
  selectedWorksiteName: string
  selectedWorksiteCity: string
  daysInOffice: number
  onDaysInOfficeChange: (daysInOffice: number) => void
  numberOfEmployees: number
  carbonImpact: {
    carImpact: number
    micromobilityImpact: number
    transitImpact: number
  }
}

const MapStats: React.FC<MapStatsProps> = (props) => {
  const {
    carReach,
    micromobilityReach,
    transitReach,
    getCityCosts,
    selectedWorksiteName,
    selectedWorksiteCity = 'Other (Default)',
    numberOfEmployees = 0,
    daysInOffice,
    onDaysInOfficeChange,
    carbonImpact,
  } = props

  const cityCosts = getCityCosts(selectedWorksiteCity, daysInOffice)

  // Calculate worksite overview metrics
  const altReach = Math.max(transitReach, micromobilityReach) || 0

  const drivingCarbon = carbonImpact.carImpact
  const transitCarbon = carbonImpact.transitImpact
  const micromobilityCarbon = carbonImpact.micromobilityImpact
  const carbonEmissionsSaved =
    drivingCarbon - (transitCarbon + micromobilityCarbon)

  // Calculate savings - exclude micromobility to match table logic
  const transitEmployeeSavings =
    getEmployeeSavings(cityCosts.transit, transitReach, numberOfEmployees) || 0
  const carEmployeeSavings =
    getEmployeeSavings(cityCosts.car, carReach, numberOfEmployees) || 0
  const transitEmployerSavings =
    getEmployerSavings(cityCosts.transit, transitReach, numberOfEmployees) || 0
  const carEmployerSavings =
    getEmployerSavings(cityCosts.car, carReach, numberOfEmployees) || 0

  // Only include transit savings in totals (exclude micromobility like in table)
  const totalEmployeeTaxSavings =
    Number(transitEmployeeSavings) + Number(carEmployeeSavings) || 0
  const totalEmployerTaxSavings =
    Number(transitEmployerSavings) + Number(carEmployerSavings) || 0

  const daysInOfficeItems = [
    { id: '2', text: '2 days in office' },
    { id: '3', text: '3 days in office' },
    { id: '4', text: '4 days in office' },
    { id: '5', text: '5 days in office' },
  ]

  const handleDaysInOfficeChange = (item: { id: string; text: string }) => {
    onDaysInOfficeChange(parseInt(item.id))
  }

  return (
    <StatsContainer>
      <Box>
        <StatsHeader>
          <TitleContainer>
            <TitleLeft>
              <Text variant='action1' mb='0.5rem' marginRight='0.5rem'>
                Commute Analysis:
              </Text>
              <Text
                variant='action1'
                mb='0.5rem'
                color={palette.secondary.purple600}
              >
                {selectedWorksiteName}
              </Text>
            </TitleLeft>
            <DaysInOfficeContainer>
              <Dropdown
                items={daysInOfficeItems}
                active={daysInOffice.toString()}
                itemCallback={handleDaysInOfficeChange}
                placeholder='Select days in office'
                variant='contained'
              />
            </DaysInOfficeContainer>
          </TitleContainer>
          <Text
            variant='body2'
            textColor='text.secondary'
            mb='0.5rem'
            fontWeight='500'
          >
            Location: {selectedWorksiteCity} • {numberOfEmployees} employees
          </Text>

          {/* Worksite Overview Grid */}
          <WorksiteOverviewContainer>
            <OverviewItem>
              <OverviewBox color='gray'>
                <Text variant='h4' fontWeight='700' mb='0.25rem'>
                  {altReach}%
                </Text>
                <Text variant='body2' fontWeight='600'>
                  Alt Reach
                </Text>
              </OverviewBox>
            </OverviewItem>

            <OverviewItem>
              <OverviewBox color='green'>
                <Text variant='h4' fontWeight='700' mb='0.25rem'>
                  {formatNumber(carbonEmissionsSaved)}
                </Text>
                <Text variant='body2' fontWeight='600'>
                  Carbon Saved
                </Text>
              </OverviewBox>
            </OverviewItem>

            <OverviewItem>
              <OverviewBox color='blue'>
                <Text variant='h4' fontWeight='700' mb='0.25rem'>
                  ${formatNumber(totalEmployerTaxSavings)}
                </Text>
                <Text variant='body2' fontWeight='600'>
                  Employer Savings
                </Text>
              </OverviewBox>
            </OverviewItem>

            <OverviewItem>
              <OverviewBox color='pink'>
                <Text variant='h4' fontWeight='700' mb='0.25rem'>
                  ${formatNumber(totalEmployeeTaxSavings)}
                </Text>
                <Text variant='body2' fontWeight='600'>
                  Employee Savings
                </Text>
              </OverviewBox>
            </OverviewItem>
          </WorksiteOverviewContainer>
        </StatsHeader>
      </Box>
    </StatsContainer>
  )
}

export default MapStats
